'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { PRODUCT_INFO_QUICK_VIEW_MENU_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useModalActions } from '@headless-workspace/core-ui';
import { GlowButton, GlowModal, GlowTextBody, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import {
    ProductDetailPricesContainer,
    ProductDetailPushLoyalty,
    ProductVisualMobile,
    SelectorAddToBasketButton,
    SelectorAddToBasketButtonContext,
    useClientData,
    useNavigation,
    useProductDetails,
    useProductDetailsUtils,
    usePromotionInfo,
    VariantSelectorList,
} from '../../../client';
import { VipBrandsValue } from '../../../server';
import { QuickViewProductMenuSkeleton } from './QuickViewProductMenuSkeleton';

type QuickViewProductMenuProps = PropsWithLocale & {
    productSku: string;
    vipBrands: VipBrandsValue;
};

export const QuickViewProductMenu: Component<QuickViewProductMenuProps> = ({ productSku, vipBrands, locale }) => {
    const { close: closeMenu } = useModalActions();

    const t = useTranslations('Discover.Pdp');
    const navigation = useNavigation();

    const { getClientFlags } = useClientData();
    const isPriceLegalMentionEnabled = getClientFlags('isPriceExceptionLegalMentionsDisplayed');

    const [selectedSku, setSelectedSku] = useState<string>(productSku);
    const { isLoading: isProductDetailsInfoLoading, result: productDetailsInfoResult } = useProductDetails(selectedSku);

    const { isProductDetailsLoading, product } = useMemo(
        () => ({
            isProductDetailsLoading: isProductDetailsInfoLoading,
            product: unwrapResultSuccess(productDetailsInfoResult),
        }),
        [isProductDetailsInfoLoading, productDetailsInfoResult],
    );

    const { productSelectionTitle, getStockInformation } = useProductDetailsUtils({ type: product?.type });

    const promotionInformation = usePromotionInfo(product?.promotion);

    useEffect(() => {
        setSelectedSku(productSku);
    }, [productSku]);

    const closeProductInformationModal = useCallback(() => {
        closeMenu(PRODUCT_INFO_QUICK_VIEW_MENU_ID);
    }, [closeMenu]);

    useEffect(() => {
        if (!product && !isProductDetailsLoading) {
            closeMenu(PRODUCT_INFO_QUICK_VIEW_MENU_ID, true);
        }
    }, [closeMenu, isProductDetailsLoading, product]);

    if (isProductDetailsLoading) {
        return <QuickViewProductMenuSkeleton />;
    }

    if (!product) {
        return null;
    }

    const fontFamily = product.isArialFont ? 'arial' : 'brand';
    const discountPercentageLabel = t('information.price.label.discountPercentage', {
        discountPercentage: product.discountPercentage,
    });

    const onClickUpdateSku = (sku: string) => {
        setSelectedSku(sku);
    };

    const redirectToProduct = () => {
        if (product.url) {
            navigation.push(product.url);
        }
    };

    return (
        <GlowModal.Container title={productSelectionTitle} onClick={closeProductInformationModal}>
            <GlowModal.Content>
                <ProductVisualMobile
                    medias={product.medias}
                    wrapperClassName={'w-[21.438rem] tablet:w-[27.5rem]'}
                    visualClassName={'w-[16.438rem] tablet:w-[22.5rem]'}
                    onProductVisualClick={redirectToProduct}
                    prevBtnAriaLabel={t('carousel.ariaLabel.prevBtn')}
                    nextBtnAriaLabel={t('carousel.ariaLabel.nextBtn')}
                    thumbArialLabel={t('carousel.ariaLabel.thumb')}
                    socialProof={product.socialProof}
                />
                <div className={'flex flex-col gap-0.5'}>
                    <ProductDetailPricesContainer
                        price={product.price}
                        priorPrice={product.priorPrice}
                        originalPrice={product.originalPrice}
                        pricePerUnit={product.pricePerUnit}
                        currency={product.currency}
                        locale={locale}
                        fontFamily={fontFamily}
                        promotion={product.promotion}
                        discountPercentageLabel={discountPercentageLabel}
                        priceAlwaysBlack={product.priceAlwaysBlack}
                        enableDiscountInfo={product.enableDiscountInfo}
                        promotionInformation={promotionInformation}
                        isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                    />
                </div>
                <GlowTextBody
                    text={t('information.selector.modal.label.products', {
                        variantCount: product.variants.length,
                    })}
                    fontWeight={'bold'}
                />
                <VariantSelectorList
                    locale={locale}
                    type={product.type}
                    variants={product.variants}
                    squaredShade={product.squaredShade}
                    currency={product.currency}
                    fontFamily={fontFamily}
                    onVariantSelected={onClickUpdateSku}
                    selectedVariantId={selectedSku}
                    getStockInformation={getStockInformation}
                    isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                    enableDiscountInfo={product.enableDiscountInfo}
                />
                {product.loyalty?.pointsToEarn && (
                    <ProductDetailPushLoyalty
                        fontFamily={fontFamily}
                        locale={locale}
                        currency={product.currency}
                        isVipBrand={product.isVipBrand}
                        hasBackCta={true}
                        loyaltyInformation={product.loyalty}
                    />
                )}
            </GlowModal.Content>
            <GlowModal.Footer>
                <SelectorAddToBasketButton
                    productType={product.type}
                    vipBrands={vipBrands}
                    isVisible={true}
                    hasShortLabel={false}
                    fontFamily={fontFamily}
                    locale={locale}
                    productsToAdd={[selectedSku]}
                    width={'full'}
                    context={SelectorAddToBasketButtonContext.Modal}
                />
                <GlowButton
                    variant={'secondary'}
                    href={product?.url ?? ''}
                    label={t('productInfo.productTab.action.discover')}
                    fontFamily={fontFamily}
                    width={'full'}
                />
            </GlowModal.Footer>
        </GlowModal.Container>
    );
};
